import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Pagination,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  DialogContentText,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material'
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  FileUpload as UploadIcon,
  FileDownload as DownloadIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where, 
  limit, 
  startAfter, 
  DocumentSnapshot, 
  serverTimestamp,
  getDoc
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../../firebase'
import { compressImageForUpload } from '../../utils/compressImage'
import { localizeCategory } from '../../utils/categoryResolver'

interface Product {
  id: string
  name: string
  category: string
  description: string
  price: number
  brand: string
  imageUrls: string[]
  isActive: boolean
  status: 'pending' | 'approved' | 'rejected'
  sellerId: string
  createdAt: any
  updatedAt: any
}

interface Category {
  id: string
  name: string
}

export default function ProductsPage() {
  const uiLang: 'ar' | 'en' = (localStorage.getItem('i18nextLng') || '').startsWith('en') ? 'en' : 'ar'
  // State management
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalProducts, setTotalProducts] = useState(0)
  const itemsPerPage = 10
  
  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  
  // Selected items
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  
  // Form data
  const [editingProduct, setEditingProduct] = useState<Partial<Product>>({})
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    category: '',
    description: '',
    price: 0,
    brand: '',
    imageUrls: [],
    isActive: true,
    status: 'pending'
  })
  
  // Notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  })
  
  // File upload
  const [uploadingImage, setUploadingImage] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)

  // Load data on component mount
  useEffect(() => {
    // Load products and categories
    loadProducts()
    loadCategories()
  }, [])

  // Filter products when search/filter changes
  useEffect(() => {
    filterProducts()
  }, [products, searchQuery, selectedCategory, statusFilter, activeFilter])

  // Update pagination when filtered products change
  useEffect(() => {
    const total = Math.ceil(filteredProducts.length / itemsPerPage)
    setTotalPages(total)
    setTotalProducts(filteredProducts.length)
  }, [filteredProducts])

  const loadProducts = async () => {
    try {
      console.log('🔄 Starting to load products...')
      setLoading(true)
      console.log('📡 Connecting to Firebase...')
      const productsRef = collection(db, 'products')
      console.log('📋 Creating query...')
      const q = query(productsRef, orderBy('createdAt', 'desc'))
      console.log('🔍 Executing query...')
      const snapshot = await getDocs(q)
      console.log('📊 Query executed, processing results...')
      
      const rawProductsData = snapshot.docs.map((doc, index) => {
        const data = doc.data()
        const product = {
          id: doc.id || `product-${index}`, // Ensure ID exists
          name: data.name || 'منتج بدون اسم',
          category: data.category || 'عام',
          description: data.description || '',
          price: data.price || 0,
          brand: data.brand || '',
          imageUrls: data.imageUrls || [],
          isActive: data.isActive !== undefined ? data.isActive : true,
          status: data.status || 'pending',
          sellerId: data.sellerId || 'unknown',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        } as Product
        console.log('🔍 Product loaded:', { id: product.id, name: product.name })
        return product
      })
      const productsData = await Promise.all(rawProductsData.map(async (product) => ({
        ...product,
        category: await localizeCategory(product.category || '', uiLang)
      })))
      
      console.log('✅ Products processed:', productsData.length)
      console.log('🔍 First few products:', productsData.slice(0, 3).map(p => ({ id: p.id, name: p.name })))
      setProducts(productsData)
      console.log('🎉 Products loaded successfully:', productsData.length)
      showSnackbar(`تم تحميل ${productsData.length} منتج بنجاح`, 'success')
    } catch (error) {
      console.error('❌ Error loading products:', error)
      console.error('Error details:', {
        message: (error as Error).message,
        code: (error as any).code,
        stack: (error as Error).stack
      })
      showSnackbar('خطأ في تحميل المنتجات: ' + (error as Error).message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      console.log('🔄 Loading categories...')
      const categoriesRef = collection(db, 'categories')
      const snapshot = await getDocs(categoriesRef)
      
      const categoriesData = snapshot.docs.map(doc => {
        const data = doc.data() as any
        const nameAr = String(data?.name ?? '').trim()
        const nameEn = String(data?.nameEn ?? data?.name_en ?? '').trim()
        return {
          id: doc.id,
          name: uiLang === 'en' ? (nameEn || nameAr) : (nameAr || nameEn)
        } as Category
      })
      
      setCategories(categoriesData)
      console.log('✅ Categories loaded successfully:', categoriesData.length)
      console.log('📋 Categories:', categoriesData.map(c => c.name))
    } catch (error) {
      console.error('❌ Error loading categories:', error)
      // Create default categories if none exist
      const defaultCategories = [
        { id: '1', name: 'إلكترونيات' },
        { id: '2', name: 'ملابس' },
        { id: '3', name: 'أطعمة' },
        { id: '4', name: 'منزل' },
        { id: '5', name: 'رياضة' }
      ]
      setCategories(defaultCategories)
      console.log('📋 Using default categories:', defaultCategories.length)
    }
  }

  const filterProducts = () => {
    let filtered = [...products]
    console.log('🔍 filterProducts - initial products:', products.length)

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchQuery.toLowerCase())
      )
      console.log('🔍 After search filter:', filtered.length)
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory)
      console.log('🔍 After category filter:', filtered.length)
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(product => product.status === statusFilter)
      console.log('🔍 After status filter:', filtered.length)
    }

    // Active filter
    if (activeFilter) {
      const isActive = activeFilter === 'active'
      filtered = filtered.filter(product => product.isActive === isActive)
      console.log('🔍 After active filter:', filtered.length)
    }

    console.log('🔍 Final filtered products:', filtered.length, 'First few:', filtered.slice(0, 3).map(p => ({ id: p.id, name: p.name })))
    setFilteredProducts(filtered)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const getPaginatedProducts = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginated = filteredProducts.slice(startIndex, endIndex)
    console.log('🔍 getPaginatedProducts:', {
      totalProducts: products.length,
      filteredProducts: filteredProducts.length,
      paginated: paginated.length,
      firstProduct: paginated[0] ? { id: paginated[0].id, name: paginated[0].name } : null
    })
    return paginated
  }

  const handleEdit = (product: Product) => {
    setEditingProduct(product)
    setEditDialogOpen(true)
  }

  const handleCreate = () => {
    setNewProduct({
      name: '',
      category: '',
      description: '',
      price: 0,
      brand: '',
      imageUrls: [],
      isActive: true,
      status: 'pending'
    })
    setCreateDialogOpen(true)
  }

  const handleDelete = (product: Product) => {
    setSelectedProduct(product)
    setDeleteDialogOpen(true)
  }

  const handleImageUpload = (product: Product) => {
    setSelectedProduct(product)
    setImageDialogOpen(true)
  }

  const handleToggleActive = async (product: Product) => {
    try {
      console.log('🔄 Toggling product active status:', { 
        id: product.id, 
        name: product.name, 
        isActive: product.isActive
      })
      
      if (!product.id) {
        console.error('❌ Product ID is missing!')
        showSnackbar('خطأ: معرف المنتج غير موجود', 'error')
        return
      }
      
      const newActiveStatus = !product.isActive
      console.log('📡 Updating product in Firebase...', { id: product.id, newActiveStatus })
      
      await updateDoc(doc(db, 'products', product.id), {
        isActive: newActiveStatus,
        updatedAt: serverTimestamp()
      })
      
      console.log('✅ Product updated successfully')
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === product.id ? { ...p, isActive: newActiveStatus } : p
      ))
      
      showSnackbar(
        newActiveStatus ? 'تم تفعيل المنتج' : 'تم إلغاء تفعيل المنتج', 
        'success'
      )
    } catch (error) {
      console.error('❌ Error updating product:', error)
      showSnackbar('خطأ في تحديث المنتج: ' + (error as Error).message, 'error')
    }
  }

  const handleStatusChange = async (product: Product, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      console.log('🔄 Changing product status:', { id: product.id, name: product.name, newStatus })
      
      if (!product.id) {
        console.error('❌ Product ID is missing!')
        showSnackbar('خطأ: معرف المنتج غير موجود', 'error')
        return
      }
      
      await updateDoc(doc(db, 'products', product.id), {
        status: newStatus,
        isActive: newStatus === 'approved',
        updatedAt: serverTimestamp()
      })
      
      console.log('✅ Product status updated successfully')
      
      setProducts(prev => prev.map(p => 
        p.id === product.id ? { ...p, status: newStatus, isActive: newStatus === 'approved' } : p
      ))
      
      const statusText = newStatus === 'approved' ? 'مقبول' : newStatus === 'rejected' ? 'مرفوض' : 'في الانتظار'
      showSnackbar(`تم تحديث حالة المنتج إلى: ${statusText}`, 'success')
    } catch (error) {
      console.error('❌ Error updating product status:', error)
      showSnackbar('خطأ في تحديث حالة المنتج: ' + (error as Error).message, 'error')
    }
  }

  const handleSaveEdit = async () => {
    if (!editingProduct.id) {
      showSnackbar('خطأ: معرف المنتج غير موجود', 'error')
      return
    }

    try {
      console.log('🔄 Saving product edit:', editingProduct)
      
      if (!editingProduct.id) {
        console.error('❌ Product ID is missing!')
        showSnackbar('خطأ: معرف المنتج غير موجود', 'error')
        return
      }
      
      // Validate required fields
      if (!editingProduct.name || editingProduct.name.trim() === '') {
        showSnackbar('يرجى إدخال اسم المنتج', 'error')
        return
      }
      
      const updateData = {
        name: editingProduct.name,
        category: editingProduct.category || '',
        description: editingProduct.description || '',
        price: editingProduct.price || 0,
        brand: editingProduct.brand || '',
        updatedAt: serverTimestamp()
      }
      
      console.log('📡 Updating product in Firebase...', { id: editingProduct.id, updateData })
      
      await updateDoc(doc(db, 'products', editingProduct.id), updateData)
      
      console.log('✅ Product updated successfully')
      
      setProducts(prev => prev.map(p => 
        p.id === editingProduct.id ? { ...p, ...updateData } : p
      ))
      
      setEditDialogOpen(false)
      showSnackbar('تم حفظ التعديلات بنجاح', 'success')
    } catch (error) {
      console.error('❌ Error updating product:', error)
      showSnackbar('خطأ في حفظ التعديلات: ' + (error as Error).message, 'error')
    }
  }

  const handleCreateProduct = async () => {
    if (!newProduct.name || newProduct.name.trim() === '') {
      showSnackbar('يرجى إدخال اسم المنتج', 'error')
      return
    }

    try {
      console.log('🔄 Creating new product:', newProduct)
      
      const productData = {
        name: newProduct.name,
        category: newProduct.category || '',
        description: newProduct.description || '',
        price: newProduct.price || 0,
        brand: newProduct.brand || '',
        imageUrls: newProduct.imageUrls || [],
        isActive: newProduct.isActive !== undefined ? newProduct.isActive : true,
        status: newProduct.status || 'pending',
        sellerId: 'admin', // Default seller ID for admin-created products
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      
      console.log('📡 Adding product to Firebase...', productData)
      
      const docRef = await addDoc(collection(db, 'products'), productData)
      
      console.log('✅ Product created successfully with ID:', docRef.id)
      
      // Add to local state
      const newProductWithId = { ...productData, id: docRef.id }
      setProducts(prev => [newProductWithId, ...prev])
      
      setCreateDialogOpen(false)
      showSnackbar('تم إضافة المنتج بنجاح', 'success')
    } catch (error) {
      console.error('❌ Error creating product:', error)
      showSnackbar('خطأ في إضافة المنتج: ' + (error as Error).message, 'error')
    }
  }

  const handleDeleteProduct = async () => {
    if (!selectedProduct) {
      showSnackbar('خطأ: لم يتم اختيار منتج للحذف', 'error')
      return
    }

    try {
      console.log('🔄 Deleting product:', { id: selectedProduct.id, name: selectedProduct.name })
      
      if (!selectedProduct.id) {
        console.error('❌ Product ID is missing!')
        showSnackbar('خطأ: معرف المنتج غير موجود', 'error')
        return
      }
      
      console.log('📡 Deleting product from Firebase...', selectedProduct.id)
      
      await deleteDoc(doc(db, 'products', selectedProduct.id))
      
      console.log('✅ Product deleted successfully')
      
      // Remove from local state
      setProducts(prev => prev.filter(p => p.id !== selectedProduct.id))
      setDeleteDialogOpen(false)
      showSnackbar(`تم حذف المنتج "${selectedProduct.name}" بنجاح`, 'success')
    } catch (error) {
      console.error('❌ Error deleting product:', error)
      showSnackbar('خطأ في حذف المنتج: ' + (error as Error).message, 'error')
    }
  }

  const handleImageUploadFile = async (file: File) => {
    if (!selectedProduct) return

    try {
      setUploadingImage(true)
      
      // رفع الصورة إلى Firebase Storage
      const optimized = await compressImageForUpload(file, 'product')
      const timestamp = Date.now()
      const fileName = `${timestamp}_${optimized.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
      const storageRef = ref(storage, `products/${selectedProduct.id}/${fileName}`)
      
      console.log('📤 بدء رفع الصورة إلى Firebase Storage...', fileName)
      
      // رفع الملف
      await uploadBytes(storageRef, optimized)
      console.log('✅ تم رفع الصورة بنجاح')
      
      // الحصول على رابط التحميل
      const imageUrl = await getDownloadURL(storageRef)
      console.log('✅ تم الحصول على رابط التحميل:', imageUrl)
      
      const updatedImageUrls = [...(selectedProduct.imageUrls || []), imageUrl]
      
      // حفظ رابط الصورة في Firestore
      await updateDoc(doc(db, 'products', selectedProduct.id), {
        imageUrls: updatedImageUrls,
        updatedAt: serverTimestamp()
      })
      
      console.log('✅ تم حفظ رابط الصورة في Firestore')
      
      setProducts(prev => prev.map(p => 
        p.id === selectedProduct.id ? { ...p, imageUrls: updatedImageUrls } : p
      ))
      
      setImageDialogOpen(false)
      showSnackbar('تم رفع الصورة بنجاح', 'success')
    } catch (error) {
      console.error('❌ خطأ في رفع الصورة:', error)
      showSnackbar('خطأ في رفع الصورة: ' + (error as Error).message, 'error')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleImportProducts = async () => {
    if (!importFile) return

    try {
      console.log('Importing products from file:', importFile.name)
      const text = await importFile.text()
      const productsData = JSON.parse(text)
      
      // Validate and add products
      for (const productData of productsData) {
        await addDoc(collection(db, 'products'), {
          ...productData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      }
      
      setImportDialogOpen(false)
      loadProducts()
      showSnackbar(`تم استيراد ${productsData.length} منتج بنجاح`, 'success')
    } catch (error) {
      console.error('Error importing products:', error)
      showSnackbar('خطأ في استيراد المنتجات: ' + (error as Error).message, 'error')
    }
  }

  const handleExportProducts = () => {
    const dataStr = JSON.stringify(filteredProducts, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    
    const exportFileDefaultName = `products_${new Date().toISOString().split('T')[0]}.json`
    
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
    
    showSnackbar('تم تصدير المنتجات بنجاح', 'success')
  }

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success'
      case 'pending': return 'warning'
      case 'rejected': return 'error'
      default: return 'default'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'مقبول'
      case 'pending': return 'في الانتظار'
      case 'rejected': return 'مرفوض'
      default: return 'غير معروف'
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Debug Info */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>معلومات التشخيص:</strong><br/>
          إجمالي المنتجات: {products.length}<br/>
          المنتجات المفلترة: {filteredProducts.length}<br/>
          الفئات المتاحة: {categories.length}<br/>
          حالة التحميل: {loading ? 'جاري التحميل' : 'مكتمل'}<br/>
          أول منتج: {products[0] ? `${products[0].name} (ID: ${products[0].id})` : 'لا توجد منتجات'}
        </Typography>
      </Alert>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          إدارة المنتجات
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            استيراد
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportProducts}
          >
            تصدير
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            إضافة منتج
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                إجمالي المنتجات
              </Typography>
              <Typography variant="h4">
                {totalProducts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                المنتجات النشطة
              </Typography>
              <Typography variant="h4" color="success.main">
                {products.filter(p => p.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                في الانتظار
              </Typography>
              <Typography variant="h4" color="warning.main">
                {products.filter(p => p.status === 'pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                مرفوضة
              </Typography>
              <Typography variant="h4" color="error.main">
                {products.filter(p => p.status === 'rejected').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="البحث في المنتجات..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>الفئة</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="الفئة"
                >
                  <MenuItem value="">الكل</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="الحالة"
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="pending">في الانتظار</MenuItem>
                  <MenuItem value="approved">مقبول</MenuItem>
                  <MenuItem value="rejected">مرفوض</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>النشاط</InputLabel>
                <Select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  label="النشاط"
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadProducts}
                fullWidth
              >
                تحديث
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedProducts.length > 0 && selectedProducts.length < getPaginatedProducts().length}
                      checked={selectedProducts.length === getPaginatedProducts().length && getPaginatedProducts().length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts(getPaginatedProducts().map(p => p.id))
                        } else {
                          setSelectedProducts([])
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>الصورة</TableCell>
                  <TableCell>الاسم</TableCell>
                  <TableCell>الفئة</TableCell>
                  <TableCell>العلامة التجارية</TableCell>
                  <TableCell>السعر</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>النشاط</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getPaginatedProducts().map((product) => (
                  <TableRow key={product.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProducts([...selectedProducts, product.id])
                          } else {
                            setSelectedProducts(selectedProducts.filter(id => id !== product.id))
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Avatar
                        src={product.imageUrls?.[0]}
                        sx={{ width: 50, height: 50 }}
                      >
                        <ImageIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {product.description?.substring(0, 50)}...
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        ID: {product.id || 'غير محدد'}
                      </Typography>
                    </TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>{product.brand}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {product.price} ريال
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(product.status)}
                        color={getStatusColor(product.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={product.isActive}
                        onChange={() => handleToggleActive(product)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="تعديل">
                          <IconButton size="small" onClick={() => handleEdit(product)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="رفع صورة">
                          <IconButton size="small" onClick={() => handleImageUpload(product)}>
                            <ImageIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="قبول">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleStatusChange(product, 'approved')}
                            disabled={product.status === 'approved'}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="رفض">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleStatusChange(product, 'rejected')}
                            disabled={product.status === 'rejected'}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton size="small" color="error" onClick={() => handleDelete(product)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="large"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>تعديل المنتج</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم المنتج"
                value={editingProduct.name || ''}
                onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="العلامة التجارية"
                value={editingProduct.brand || ''}
                onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>الفئة</InputLabel>
                <Select
                  value={editingProduct.category || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                  label="الفئة"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="السعر"
                type="number"
                value={editingProduct.price || 0}
                onChange={(e) => setEditingProduct({ ...editingProduct, price: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف"
                multiline
                rows={3}
                value={editingProduct.description || ''}
                onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={handleSaveEdit}>حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>إضافة منتج جديد</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم المنتج"
                value={newProduct.name || ''}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="العلامة التجارية"
                value={newProduct.brand || ''}
                onChange={(e) => setNewProduct({ ...newProduct, brand: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>الفئة</InputLabel>
                <Select
                  value={newProduct.category || ''}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  label="الفئة"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="السعر"
                type="number"
                value={newProduct.price || 0}
                onChange={(e) => setNewProduct({ ...newProduct, price: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف"
                multiline
                rows={3}
                value={newProduct.description || ''}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={handleCreateProduct}>إضافة</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف المنتج "{selectedProduct?.name}"؟ هذا الإجراء لا يمكن التراجع عنه.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" color="error" onClick={handleDeleteProduct}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)}>
        <DialogTitle>رفع صورة للمنتج</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                handleImageUploadFile(file)
              }
            }}
            style={{ marginTop: 16 }}
          />
          {uploadingImage && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>إلغاء</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>استيراد المنتجات</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            يرجى اختيار ملف JSON يحتوي على بيانات المنتجات المراد استيرادها.
          </DialogContentText>
          <input
            type="file"
            accept=".json"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                setImportFile(file)
              }
            }}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>إلغاء</Button>
          <Button 
            variant="contained" 
            onClick={handleImportProducts}
            disabled={!importFile}
          >
            استيراد
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}